import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/config";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Leaf, Trash2, Layers, Sprout, HandCoins, Loader2, ShieldCheck, Wrench, ChevronDown, ChevronUp } from "lucide-react";

const BUSH_SIZES = [
  { key: "small",  label: "Small bush",  desc: "Up to 3 ft", rate: 15 },
  { key: "medium", label: "Medium bush", desc: "3–5 ft",    rate: 25 },
  { key: "large",  label: "Large bush",  desc: "5–8 ft",    rate: 40 },
  { key: "xl",     label: "XL bush",     desc: "8+ ft",     rate: 60 },
];
const BUSH_COUNT_TIERS = [
  { key: "small_job",  label: "Small (1–5)",     min: 1,  max: 5,  defaultCount: 3  },
  { key: "medium_job", label: "Medium (6–10)",   min: 6,  max: 10, defaultCount: 8  },
  { key: "large_job",  label: "Large (11–20)",   min: 11, max: 20, defaultCount: 15 },
  { key: "xl_job",     label: "Extra Large (20+)", min: 21, max: 50, defaultCount: 25 },
];
const HAUL_AWAY = [
  { key: "none",  label: "No haul-away", desc: "I'll keep the clippings" },
  { key: "small", label: "Small load (+$25)", desc: "Light debris" },
  { key: "large", label: "Large load (+$50)", desc: "Multiple bushes" },
];

const CLEANUP_INTENSITIES = [
  { key: "light",    label: "Light",    desc: "Quick tidy-up",  pct: "1.0x" },
  { key: "standard", label: "Standard", desc: "Routine reset",  pct: "+50%" },
  { key: "heavy",    label: "Heavy",    desc: "Built-up debris", pct: "+100%" },
  { key: "extreme",  label: "Extreme",  desc: "Full overhaul",  pct: "+200%" },
];

const MULCH_BED_TIERS = [
  { key: "under_100",  label: "Under 100 sqft", desc: "Small bed",    range: "$80–$150" },
  { key: "100_to_250", label: "100–250 sqft",   desc: "Standard bed", range: "$150–$300" },
  { key: "250_to_500", label: "250–500 sqft",   desc: "Large bed",    range: "$300–$600" },
  { key: "500_plus",   label: "500+ sqft",      desc: "Multi-bed",    range: "$600–$1,200+" },
];

const LANDSCAPE_SIZES = [
  { key: "small",  label: "Small",  desc: "1–2 features",  range: "$150–$500" },
  { key: "medium", label: "Medium", desc: "3–5 features",  range: "$500–$1,500" },
  { key: "large",  label: "Large",  desc: "Full redesign", range: "$1,500–$5,000" },
  { key: "custom", label: "Custom", desc: "Quote-only",    range: "Admin quote" },
];
const LANDSCAPE_FEATURES = [
  { key: "flower_planting",    label: "Flower planting" },
  { key: "rock_installation",  label: "Rock installation" },
  { key: "mulch_installation", label: "Mulch installation" },
  { key: "bed_redesign",       label: "Bed redesign" },
  { key: "sod_installation",   label: "Sod installation" },
  { key: "shrub_planting",     label: "Shrub planting" },
];

const SERVICE_META = {
  hedge_trimming: { Icon: Leaf,    title: "Bush / Hedge Trimming" },
  yard_cleanup:   { Icon: Trash2,  title: "Yard Clean-up" },
  mulching:       { Icon: Layers,  title: "Mulching" },
  landscaping:    { Icon: Sprout,  title: "Landscaping" },
};

export default function QuoteRangeBuilder({ serviceKey, onSubmit }) {
  const meta = SERVICE_META[serviceKey] || { Icon: Sprout, title: "Quote" };
  const [range, setRange] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Bush
  const [bushTier, setBushTier] = useState("medium_job");
  const [bushCount, setBushCount] = useState(8);
  const [bushSize, setBushSize] = useState("medium");
  const [bushOvergrown, setBushOvergrown] = useState(false);
  const [bushDifficult, setBushDifficult] = useState(false);
  const [bushHaul, setBushHaul] = useState("none");

  // Cleanup
  const [cleanupSqft, setCleanupSqft] = useState(5000);
  const [cleanupIntensity, setCleanupIntensity] = useState("standard");

  // Mulch
  const [mulchTier, setMulchTier] = useState("100_to_250");

  // Landscape
  const [projectSize, setProjectSize] = useState("medium");
  const [projectFeatures, setProjectFeatures] = useState([]);

  const payload = useMemo(() => {
    if (serviceKey === "hedge_trimming") {
      return { service_key: serviceKey, bush_count: bushCount, bush_size: bushSize,
               bush_overgrown: bushOvergrown, bush_difficult_access: bushDifficult, bush_haul_away: bushHaul };
    }
    if (serviceKey === "yard_cleanup") {
      return { service_key: serviceKey, cleanup_sqft: cleanupSqft, cleanup_intensity: cleanupIntensity };
    }
    if (serviceKey === "mulching") {
      return { service_key: serviceKey, mulch_bed_tier: mulchTier };
    }
    if (serviceKey === "landscaping") {
      return { service_key: serviceKey, project_size: projectSize, project_features: projectFeatures };
    }
    return null;
  }, [serviceKey, bushCount, bushSize, bushOvergrown, bushDifficult, bushHaul, cleanupSqft, cleanupIntensity, mulchTier, projectSize, projectFeatures]);

  useEffect(() => {
    if (!payload) return;
    let cancel = false;
    setLoading(true);
    api.post("/quotes/range", payload).then(({ data }) => {
      if (!cancel) setRange(data);
    }).catch(() => {
      if (!cancel) setRange(null);
    }).finally(() => {
      if (!cancel) setLoading(false);
    });
    return () => { cancel = true; };
  }, [payload]);

  const handleTierChange = (tier) => {
    setBushTier(tier.key);
    setBushCount(tier.defaultCount);
  };

  const toggleFeature = (key) => {
    setProjectFeatures((arr) => arr.includes(key) ? arr.filter((x) => x !== key) : [...arr, key]);
  };

  const Icon = meta.Icon;
  const quoteOnly = range?.quote_only;

  return (
    <div className="space-y-6" data-testid={`range-builder-${serviceKey}`}>
      {/* HEADER */}
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white flex items-center justify-center shadow-md shadow-emerald-900/20">
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-700 font-extrabold">Get a Quote</div>
          <div className="text-xl font-extrabold tracking-tight text-gray-900 font-[Outfit] leading-tight">{meta.title}</div>
        </div>
      </div>

      {/* BUSH TRIMMING */}
      {serviceKey === "hedge_trimming" && (
        <>
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-gray-500 font-bold mb-3">How many bushes?</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="bush-count-tiers">
              {BUSH_COUNT_TIERS.map((t) => {
                const active = bushTier === t.key;
                return (
                  <button key={t.key} type="button" onClick={() => handleTierChange(t)}
                    data-testid={`bush-tier-${t.key}`}
                    className={`rounded-2xl border p-3 text-left transition-all active:scale-[0.98] ${active ? "bg-gradient-to-br from-[#0A2E1E] to-[#062818] text-white border-transparent shadow-md" : "bg-white text-gray-800 border-gray-200 hover:border-gray-300"}`}>
                    <div className="text-[13px] font-extrabold font-[Outfit]">{t.label}</div>
                    <div className={`text-[11px] mt-0.5 ${active ? "text-emerald-100/80" : "text-gray-500"}`}>{t.max >= 50 ? "21+ bushes" : `${t.min}–${t.max} bushes`}</div>
                  </button>
                );
              })}
            </div>
            <div className="mt-3 bg-white rounded-2xl border border-gray-200 px-4 h-14 flex items-center gap-3" data-testid="bush-count-row">
              <span className="text-[11px] uppercase tracking-[0.18em] text-gray-500 font-bold">Bushes</span>
              <input type="number" min={1} max={50} value={bushCount}
                onChange={(e) => setBushCount(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
                data-testid="bush-count-input"
                className="flex-1 text-xl font-extrabold text-gray-900 bg-transparent outline-none tabular-nums" />
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-gray-500 font-bold mb-3">Bush size</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="bush-size-picker">
              {BUSH_SIZES.map((s) => {
                const active = bushSize === s.key;
                return (
                  <button key={s.key} type="button" onClick={() => setBushSize(s.key)}
                    data-testid={`bush-size-${s.key}`}
                    className={`rounded-2xl border p-3 text-left transition-all active:scale-[0.98] ${active ? "bg-gradient-to-br from-[#0A2E1E] to-[#062818] text-white border-transparent shadow-md" : "bg-white text-gray-800 border-gray-200 hover:border-gray-300"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <Leaf className={`w-4 h-4 ${active ? "text-emerald-300" : "text-[#10B981]"}`} />
                      <span className={`text-[9px] font-extrabold uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-full ${active ? "bg-white/15 text-emerald-100" : "bg-emerald-100 text-[#047857]"}`}>${s.rate}/ea</span>
                    </div>
                    <div className="mt-2 text-[13px] font-extrabold font-[Outfit] leading-tight">{s.label}</div>
                    <div className={`mt-0.5 text-[11px] ${active ? "text-emerald-100/80" : "text-gray-500"}`}>{s.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>
          <button type="button" onClick={() => setShowAdvanced((v) => !v)} className="flex items-center gap-1 text-[12px] font-bold text-[#047857]" data-testid="bush-advanced-toggle">
            {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {showAdvanced ? "Hide" : "Show"} job conditions
          </button>
          {showAdvanced && (
            <div className="space-y-3 rounded-2xl bg-gray-50 border border-gray-200 p-4" data-testid="bush-advanced-panel">
              <label className="flex items-center justify-between gap-3" data-testid="bush-overgrown-row">
                <div><div className="text-[13px] font-bold">Overgrown bushes (+25%)</div><div className="text-[11px] text-gray-500">Thick or unkempt growth</div></div>
                <Switch checked={bushOvergrown} onCheckedChange={setBushOvergrown} data-testid="bush-overgrown" />
              </label>
              <label className="flex items-center justify-between gap-3" data-testid="bush-difficult-row">
                <div><div className="text-[13px] font-bold">Difficult access (+15%)</div><div className="text-[11px] text-gray-500">Tight spots, ladders, slopes</div></div>
                <Switch checked={bushDifficult} onCheckedChange={setBushDifficult} data-testid="bush-difficult" />
              </label>
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500 font-bold mb-2">Haul-away</div>
                <div className="grid grid-cols-3 gap-2">
                  {HAUL_AWAY.map((h) => {
                    const active = bushHaul === h.key;
                    return (
                      <button key={h.key} type="button" onClick={() => setBushHaul(h.key)} data-testid={`bush-haul-${h.key}`}
                        className={`rounded-xl border p-2 text-center transition-all ${active ? "bg-[#062818] text-white border-transparent" : "bg-white border-gray-200"}`}>
                        <div className="text-[11px] font-extrabold">{h.label}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* YARD CLEANUP */}
      {serviceKey === "yard_cleanup" && (
        <>
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-gray-500 font-bold mb-3">Yard size (sq ft)</div>
            <div className="bg-white rounded-2xl border border-gray-200 px-5 h-16 flex items-center gap-3" data-testid="cleanup-sqft-row">
              <input inputMode="numeric" value={cleanupSqft}
                onChange={(e) => setCleanupSqft(Math.max(0, Math.min(100000, parseInt(String(e.target.value).replace(/[^0-9]/g, "")) || 0)))}
                data-testid="cleanup-sqft-input"
                className="flex-1 text-2xl font-extrabold text-gray-900 bg-transparent outline-none tabular-nums" />
              <span className="text-sm font-bold text-gray-400 uppercase tracking-wider">sq ft</span>
            </div>
            <div className="mt-2 flex gap-2 flex-wrap">
              {[1000, 2500, 5000, 10000].map((v) => (
                <button key={v} type="button" onClick={() => setCleanupSqft(v)} data-testid={`cleanup-sqft-quick-${v}`}
                  className={`px-3 h-8 rounded-full text-xs font-bold border ${cleanupSqft === v ? "border-[#047857] text-[#047857] bg-emerald-50" : "border-gray-200 text-gray-600 bg-white"}`}>
                  {v.toLocaleString()}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-gray-500 font-bold mb-3">Cleanup intensity</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="cleanup-intensity-picker">
              {CLEANUP_INTENSITIES.map((c) => {
                const active = cleanupIntensity === c.key;
                return (
                  <button key={c.key} type="button" onClick={() => setCleanupIntensity(c.key)}
                    data-testid={`cleanup-intensity-${c.key}`}
                    className={`rounded-2xl border p-3 text-left transition-all active:scale-[0.98] ${active ? "bg-gradient-to-br from-[#0A2E1E] to-[#062818] text-white border-transparent shadow-md" : "bg-white text-gray-800 border-gray-200 hover:border-gray-300"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <Trash2 className={`w-4 h-4 ${active ? "text-emerald-300" : "text-[#10B981]"}`} />
                      <span className={`text-[9px] font-extrabold uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-full ${active ? "bg-white/15 text-emerald-100" : "bg-emerald-100 text-[#047857]"}`}>{c.pct}</span>
                    </div>
                    <div className="mt-2 text-[13px] font-extrabold font-[Outfit] leading-tight">{c.label}</div>
                    <div className={`mt-0.5 text-[11px] ${active ? "text-emerald-100/80" : "text-gray-500"}`}>{c.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* MULCHING */}
      {serviceKey === "mulching" && (
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-gray-500 font-bold mb-3">Estimated bed size</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="mulch-tier-picker">
            {MULCH_BED_TIERS.map((t) => {
              const active = mulchTier === t.key;
              return (
                <button key={t.key} type="button" onClick={() => setMulchTier(t.key)}
                  data-testid={`mulch-tier-${t.key}`}
                  className={`rounded-2xl border p-4 text-left transition-all active:scale-[0.98] ${active ? "bg-gradient-to-br from-[#0A2E1E] to-[#062818] text-white border-transparent shadow-md" : "bg-white text-gray-800 border-gray-200 hover:border-gray-300"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <Layers className={`w-4 h-4 ${active ? "text-emerald-300" : "text-[#10B981]"}`} />
                    <span className={`text-[10px] font-extrabold uppercase tracking-[0.12em] px-2 py-0.5 rounded-full ${active ? "bg-white/15 text-emerald-100" : "bg-emerald-100 text-[#047857]"}`}>{t.range}</span>
                  </div>
                  <div className="mt-2 text-[14px] font-extrabold font-[Outfit] leading-tight">{t.label}</div>
                  <div className={`mt-0.5 text-[11px] ${active ? "text-emerald-100/80" : "text-gray-500"}`}>{t.desc}</div>
                </button>
              );
            })}
          </div>
          <div className="mt-3 rounded-2xl bg-amber-50 border border-amber-200 p-3 flex items-start gap-2" data-testid="mulch-material-note">
            <ShieldCheck className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
            <div className="text-[11px] text-amber-800 leading-snug">Labor only — your final quote will include actual mulch material cost + delivery, confirmed by our team.</div>
          </div>
        </div>
      )}

      {/* LANDSCAPING */}
      {serviceKey === "landscaping" && (
        <>
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-gray-500 font-bold mb-3">Project size</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="project-size-picker">
              {LANDSCAPE_SIZES.map((s) => {
                const active = projectSize === s.key;
                return (
                  <button key={s.key} type="button" onClick={() => setProjectSize(s.key)}
                    data-testid={`project-size-${s.key}`}
                    className={`rounded-2xl border p-3 text-left transition-all active:scale-[0.98] ${active ? "bg-gradient-to-br from-[#0A2E1E] to-[#062818] text-white border-transparent shadow-md" : "bg-white text-gray-800 border-gray-200 hover:border-gray-300"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <Sprout className={`w-4 h-4 ${active ? "text-emerald-300" : "text-[#10B981]"}`} />
                      <span className={`text-[9px] font-extrabold uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-full ${active ? "bg-white/15 text-emerald-100" : "bg-emerald-100 text-[#047857]"}`}>{s.range}</span>
                    </div>
                    <div className="mt-2 text-[13px] font-extrabold font-[Outfit] leading-tight">{s.label}</div>
                    <div className={`mt-0.5 text-[11px] ${active ? "text-emerald-100/80" : "text-gray-500"}`}>{s.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>
          {projectSize !== "custom" && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-gray-500 font-bold mb-3">Features to include</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="project-features-picker">
                {LANDSCAPE_FEATURES.map((f) => {
                  const active = projectFeatures.includes(f.key);
                  return (
                    <button key={f.key} type="button" onClick={() => toggleFeature(f.key)}
                      data-testid={`project-feature-${f.key}`}
                      className={`rounded-xl border p-2.5 text-left transition-all text-[12px] font-bold ${active ? "bg-emerald-600 text-white border-transparent" : "bg-white text-gray-700 border-gray-200"}`}>
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* RANGE OUTPUT */}
      <div className="rounded-3xl bg-gradient-to-br from-[#0A2E1E] via-[#093924] to-[#062818] text-white p-7 relative overflow-hidden" data-testid="quote-range-card">
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-emerald-400/10 blur-3xl" />
        <div className="absolute -bottom-12 -left-10 w-44 h-44 rounded-full bg-emerald-300/10 blur-3xl" />
        <div className="relative">
          <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-300 font-bold">Estimated Range</div>
          {loading ? (
            <div className="mt-3 flex items-center gap-2 text-emerald-200"><Loader2 className="w-5 h-5 animate-spin" /> Calculating…</div>
          ) : quoteOnly ? (
            <div className="mt-3">
              <div className="text-3xl font-extrabold tracking-tight">Custom Quote</div>
              <div className="mt-1 text-[13px] text-emerald-100/80">Our team will review your project and send you a detailed price.</div>
            </div>
          ) : range ? (
            <>
              <div className="mt-2 flex items-baseline gap-2 flex-wrap">
                <div className="text-4xl font-extrabold tracking-tight tabular-nums" data-testid="quote-range-low">${range.low.toFixed(0)}</div>
                <div className="text-2xl font-bold text-emerald-200">–</div>
                <div className="text-4xl font-extrabold tracking-tight tabular-nums" data-testid="quote-range-high">${range.high.toFixed(0)}</div>
              </div>
              <div className="mt-1 text-[12px] text-emerald-100/80">{range.description}</div>
              {range.min_offer > 0 && (
                <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-white/10 text-emerald-100" data-testid="quote-min-offer">
                  Offers from ${range.min_offer.toFixed(0)} (15% below low)
                </div>
              )}
            </>
          ) : (
            <div className="mt-3 text-[13px] text-emerald-200/70">Pick your options to see a price range.</div>
          )}

          <div className="mt-5">
            <Button onClick={() => onSubmit && onSubmit(range)}
              disabled={!range}
              data-testid="quote-submit-offer-btn"
              className="rounded-full bg-white hover:bg-emerald-50 text-[#062818] font-extrabold h-12 px-6 w-full sm:w-auto">
              <HandCoins className="w-4 h-4 mr-2" /> {quoteOnly ? "Request Custom Quote →" : "Submit My Offer →"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
